import FormInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import FormSelect from 'components/ui/composed/declarations/formSelect/DeclarationSelect';
import useTooltips from 'hooks/useTooltips';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationFormCardProps } from 'views/declarations/common/declaration.form.card';
import { getFormikProps, getFullCodelistValue, handleToggleHelp } from 'views/declarations/utils/form-utils';
import { getEoriValidation, getMaxValidation } from 'views/declarations/utils/validation-utils';
import * as Yup from 'yup';
import useCodelists from '../../../../hooks/useCodelists';
import addPathPrefix from '../../../../utils/addPathPrefix';
import { additionalFiscalReferenceObj as additionalFiscalReferenceH1 } from '../../ireland/import/h1/validation/ValidationSchema';
import { FormCardContainer } from '../cards/NewFormCard';
import MultipleItemsCard from '../MultipleItemsCard';
import SearchCustomer from '../search-customer/SearchCustomer';
import useSearchCustomer from '../search-customer/useSearchCustomer';
import { validators } from 'views/declarations/uk/export/validations/validations';

const FiscalReferenceSchema = Yup.object().shape({
    referenceId: getEoriValidation('referenceId'),
    referenceVat: getMaxValidation('referenceVat', 3).nullable(),
});

const FiscalReferenceCard: FC<
    DeclarationFormCardProps & {
        isRequired?: boolean;
    }
> = ({
    propsPathPrefix,
    getFieldProps: declarationGetFieldProps,
    getFieldHelpers: declarationGetFieldHelpers,
    viewOnly,
    defaultOpen,
    declarationCountry,
    expandAll,
    keyCard,
    toggleHelp,
    isRequired,
    formik,
}) => {
    const { getH1TooltipsByRefNumberAndField } = useTooltips();
    const { t } = useTranslation('form');
    const { aisCodelists } = useCodelists();

    const {
        handleSelectCustomer,
        show: showSearchCustomerDrawer,
        triggerShowDrawer: triggerShowSearchCustomerDrawer,
        triggerHideDrawer: triggerHideSearchCustomerDrawer,
    } = useSearchCustomer();

    const getTooltip = useCallback(
        (sadBox: string, label: string): string | undefined => getH1TooltipsByRefNumberAndField(sadBox, label),
        [getH1TooltipsByRefNumberAndField]
    );

    return (
        <MultipleItemsCard
            defaultOpen={defaultOpen}
            viewOnly={viewOnly}
            title={t('additionalFiscalReference.title')}
            keyCard={keyCard}
            initialValues={{
                roleCode: '',
                vatNumber: '',
            }}
            list={(values) => [
                {
                    field: t('referenceVat'),
                    value: getFullCodelistValue(values.roleCode, aisCodelists?.additionalFiscalRefRoleCode),
                },
                { field: t('referenceId'), value: values.vatNumber },
            ]}
            path={propsPathPrefix ?? ''}
            getFieldProps={declarationGetFieldProps}
            getFieldHelpers={declarationGetFieldHelpers}
            expandAll={expandAll}
            condensed
            formik={formik}
            required={isRequired}
            validationSchema={
                declarationCountry === DeclarationCountry.IRELAND ? additionalFiscalReferenceH1 : FiscalReferenceSchema
            }
            refNumber="3.40"
            form={(props, path) => (
                <FormCardContainer>
                    <FormSelect
                        required
                        {...getFormikProps(addPathPrefix(path, 'roleCode'), props)}
                        refNumber="3.40"
                        label={t('referenceVat')}
                        tooltip={getTooltip('3.40', t('referenceVat'))}
                        refClicked={(ref) => handleToggleHelp(ref, { toggleHelp })}
                        selectOptions={aisCodelists?.additionalFiscalRefRoleCode}
                        condensed
                        codeValidation={[validators.maxLength(3)]}
                    />
                    <FormInput
                        required
                        maxLength={17}
                        {...getFormikProps(addPathPrefix(path, 'vatNumber'), props)}
                        refNumber="3.40"
                        tooltip={getTooltip('3.40', t('referenceId'))}
                        refClicked={(ref) => handleToggleHelp(ref, { toggleHelp })}
                        label={t('referenceId')}
                        onSearchCustomer={({ fieldHelpers }) => {
                            triggerShowSearchCustomerDrawer((customer) => {
                                fieldHelpers?.setValue(customer.eori);
                            });
                        }}
                        condensed
                    />
                    <SearchCustomer.Drawer
                        onClose={triggerHideSearchCustomerDrawer}
                        show={showSearchCustomerDrawer}
                        onSelectCustomer={handleSelectCustomer}
                    />
                </FormCardContainer>
            )}
        />
    );
};
export default FiscalReferenceCard;
